import { Suspense, lazy } from 'react';
import { Navigate, Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom';

import { usePageTracking } from '@/shared/hooks/use-page-tracking';

// Eagerly load auth pages for better UX
import { AuthCallbackPage } from '@/pages/auth/callback';
import { RegisterPage } from '@/pages/auth/register';

import { AuthLayout } from '@/widgets/layouts/auth-layout';
import { BaseLayout } from '@/widgets/layouts/base-layout';

import { ROUTES } from '@/shared/config/routes';

import { Permission, ProtectedRoute } from './protected-route';

// Lazy load all other pages
const HomePage = lazy(() => import('@/pages/home').then((m) => ({ default: m.HomePage })));
const CreatePage = lazy(() => import('@/pages/create').then((m) => ({ default: m.CreatePage })));
const ImageStudioPage = lazy(() =>
  import('@/pages/image-studio').then((m) => ({ default: m.ImageStudioPage })),
);
const KontextPage = lazy(() => import('@/pages/kontext').then((m) => ({ default: m.KontextPage })));
const ModelPage = lazy(() => import('@/pages/model').then((m) => ({ default: m.ModelPage })));
const NotFoundPage = lazy(() =>
  import('@/pages/not-found').then((m) => ({ default: m.NotFoundPage })),
);
const ProfilePage = lazy(() => import('@/pages/profile').then((m) => ({ default: m.ProfilePage })));
const SubscriptionsPage = lazy(() =>
  import('@/pages/subscriptions').then((m) => ({ default: m.SubscriptionsPage })),
);
const VideoStudioPage = lazy(() =>
  import('@/pages/video-studio').then((m) => ({ default: m.VideoStudioPage })),
);
const PromptLibraryPage = lazy(() =>
  import('@/pages/prompt-library').then((m) => ({ default: m.PromptLibraryPage })),
);
const ImageEditorPage = lazy(() =>
  import('@/pages/image-editor').then((m) => ({ default: m.ImageEditorPage })),
);
const StageDesignerPage = lazy(() => import('@/pages/stage-designer'));
const StagingPage = lazy(() => import('@/pages/staging'));

// Loading component for lazy loaded pages
const PageLoader = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

// Root layout that provides router context for hooks like usePageTracking
const RootLayout = () => {
  usePageTracking();
  return <Outlet />;
};

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: ROUTES.HOME,
        element: <BaseLayout />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<PageLoader />}>
                <HomePage />
              </Suspense>
            ),
          },
          {
            path: 'model',
            handle: { title: 'Model' },
            element: (
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ModelPage />
                </Suspense>
              </ProtectedRoute>
            ),
            loader: async () => {
              // Load model data
              return null;
            },
          },
          {
            path: 'profile',
            handle: { title: 'Profile' },
            element: (
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ProfilePage />
                </Suspense>
              </ProtectedRoute>
            ),
            loader: async () => {
              // Load profile data
              return null;
            },
          },
          // Look Like route - temporarily disabled
          // {
          //   path: 'look-like',
          //   handle: { title: 'Look Like' },
          //   element: (
          //     <ProtectedRoute permissions={[Permission.AUTH, Permission.ACCOUNT_NOT_DELETED]}>
          //       <LookLikePage />
          //     </ProtectedRoute>
          //   ),
          //   loader: async () => {
          //     // Load look-like data
          //     return null;
          //   },
          // },
          {
            path: 'create',
            handle: { title: 'Create Your AI' },
            element: (
              <ProtectedRoute permissions={[Permission.AUTH, Permission.ACCOUNT_NOT_DELETED]}>
                <Suspense fallback={<PageLoader />}>
                  <CreatePage />
                </Suspense>
              </ProtectedRoute>
            ),
            loader: async () => {
              return null;
            },
          },
          {
            path: 'kontext/:kontextType',
            handle: { title: 'Kontext' },
            element: (
              <ProtectedRoute permissions={[Permission.AUTH, Permission.ACCOUNT_NOT_DELETED]}>
                <Suspense fallback={<PageLoader />}>
                  <KontextPage />
                </Suspense>
              </ProtectedRoute>
            ),
            loader: async ({ params }) => {
              // Load kontext type data for header title
              const { kontextService } = await import('@/shared/api/kontext-service');
              try {
                const kontextType = await kontextService.getKontextType({ name: params.kontextType });
                return { kontextType };
              } catch {
                return { kontextType: null };
              }
            },
          },
          {
            path: 'subscriptions',
            handle: { title: 'Subscriptions' },
            element: (
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <SubscriptionsPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'image-studio',
            handle: { title: 'Image Studio' },
            element: (
              <ProtectedRoute permissions={[Permission.AUTH, Permission.ACCOUNT_NOT_DELETED]}>
                <Suspense fallback={<PageLoader />}>
                  <ImageStudioPage />
                </Suspense>
              </ProtectedRoute>
            ),
            loader: async () => {
              return null;
            },
          },
          {
            path: 'video-studio',
            handle: { title: 'Video Studio' },
            element: (
              <ProtectedRoute permissions={[Permission.AUTH, Permission.ACCOUNT_NOT_DELETED]}>
                <Suspense fallback={<PageLoader />}>
                  <VideoStudioPage />
                </Suspense>
              </ProtectedRoute>
            ),
            loader: async () => {
              return null;
            },
          },
          {
            path: ROUTES.PROMPT_LIBRARY,
            handle: { title: 'Prompt Library' },
            element: (
              <ProtectedRoute permissions={[Permission.AUTH, Permission.ACCOUNT_NOT_DELETED]}>
                <Suspense fallback={<PageLoader />}>
                  <PromptLibraryPage />
                </Suspense>
              </ProtectedRoute>
            ),
            loader: async () => {
              return null;
            },
          },
          {
            path: ROUTES.IMAGE_EDITOR,
            handle: { title: 'Image Editor' },
            element: (
              <ProtectedRoute permissions={[Permission.AUTH, Permission.ACCOUNT_NOT_DELETED]}>
                <Suspense fallback={<PageLoader />}>
                  <ImageEditorPage />
                </Suspense>
              </ProtectedRoute>
            ),
            loader: async () => {
              return null;
            },
          },

          {
            path: ROUTES.STAGE_DESIGNER,
            handle: { title: 'Stage Designer' },
            element: (
              <ProtectedRoute permissions={[Permission.AUTH, Permission.ACCOUNT_NOT_DELETED, Permission.ADMIN]}>
                <Suspense fallback={<PageLoader />}>
                  <StageDesignerPage />
                </Suspense>
              </ProtectedRoute>
            ),
            loader: async () => {
              return null;
            },
          },
          {
            path: 'staging',
            handle: { title: 'Staging' },
            element: (
              <ProtectedRoute permissions={[Permission.AUTH, Permission.ACCOUNT_NOT_DELETED, Permission.ADMIN]}>
                <Suspense fallback={<PageLoader />}>
                  <StagingPage />
                </Suspense>
              </ProtectedRoute>
            ),
            loader: async () => {
              return null;
            },
          },
        ],
      },
      {
        path: ROUTES.AUTH.BASE,
        element: <AuthLayout />,
        children: [
          // {
          //   path: 'login',
          //   element: <LoginPage />,
          // },
          {
            path: 'sign-in',
            element: <RegisterPage />,
          },
          {
            // Redirect old /auth/register to /auth/sign-in
            path: 'register',
            element: <Navigate to="/auth/sign-in" replace />,
          },
          {
            path: 'callback',
            element: <AuthCallbackPage />,
          },
        ],
      },
      {
        path: ROUTES.NOT_FOUND,
        element: (
          <Suspense fallback={<PageLoader />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
]);

export const AppRouter = () => {
  return <RouterProvider router={router} />;
};
